import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2, ShieldCheck, Smartphone } from "lucide-react";

import { createClientRequestId } from "../../utils/clientRequestId";
import { riderApi } from "../services/riderApi";
import { getRiderDeviceInstanceId } from "../utils/riderDevice";
import { saveRiderExecutionAuthority } from "../utils/riderExecutionAuthority";

function message(error) {
  if (error?.code === "EXECUTION_AUTHORITY_HELD") return "This run is being controlled from another device.";
  if (error?.code === "EXECUTION_AUTHORITY_STALE") return "This device no longer controls the run. Refresh its access before continuing.";
  if (error?.code === "DELIVERY_POLICY_UNAVAILABLE") return "Run controls are not available yet. Ask the store to finish setup.";
  if (error?.code === "RUN_CONFLICT") return "The run changed. Refresh it before choosing a controlling device.";
  return "We couldn’t verify which device controls this run. Check your connection and try again.";
}

export default function RiderExecutionAuthorityCard({ businessId, run, onChanged }) {
  const [authority, setAuthority] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [confirmTakeover, setConfirmTakeover] = useState(false);
  const keys = useRef(new Map());
  const deviceId = getRiderDeviceInstanceId();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setAuthority(await riderApi.getExecutionAuthority(businessId, run.id, deviceId));
    } catch (requestError) {
      setError(message(requestError));
    } finally {
      setLoading(false);
    }
  }, [businessId, deviceId, run.id]);

  useEffect(() => { load(); }, [load]);

  const mutate = async (kind) => {
    if (busy) return;
    setBusy(kind);
    setError("");
    const keyName = `${kind}:${run.id}:${run.version}`;
    if (!keys.current.has(keyName)) keys.current.set(keyName, createClientRequestId(keyName));
    try {
      const result = kind === "takeover"
        ? await riderApi.takeoverExecutionAuthority(businessId, run.id, {
          deviceInstanceId: deviceId,
          expectedRunVersion: run.version,
          takeoverAcknowledged: true,
          reasonCode: "APP_RECOVERY",
          idempotencyKey: keys.current.get(keyName),
        })
        : await riderApi.claimExecutionAuthority(businessId, run.id, {
          deviceInstanceId: deviceId,
          expectedRunVersion: run.version,
          idempotencyKey: keys.current.get(keyName),
        });
      keys.current.delete(keyName);
      saveRiderExecutionAuthority(businessId, run.id, result);
      setAuthority(result);
      setConfirmTakeover(false);
      onChanged?.(result);
    } catch (requestError) {
      setError(message(requestError));
      if (["EXECUTION_AUTHORITY_HELD", "EXECUTION_AUTHORITY_STALE", "RUN_CONFLICT"].includes(requestError?.code)) {
        await load();
      }
    } finally {
      setBusy("");
    }
  };

  if (loading) {
    return <section className="rounded-3xl border border-stone-200 bg-white p-5 dark:border-white/10 dark:bg-neutral-900"><div className="flex items-center gap-3 text-sm text-stone-600 dark:text-stone-300"><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Checking this device…</div></section>;
  }

  const heldElsewhere = authority?.status === "ACTIVE" && !authority?.isCurrentDevice;
  return (
    <section className="rounded-3xl border border-stone-200 bg-white p-5 dark:border-white/10 dark:bg-neutral-900" aria-busy={Boolean(busy)}>
      <div className="flex items-start gap-3">
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-2xl ${authority?.isCurrentDevice ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300" : "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"}`}>
          {authority?.isCurrentDevice ? <ShieldCheck className="h-5 w-5" aria-hidden="true" /> : <Smartphone className="h-5 w-5" aria-hidden="true" />}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold">Controlling device</h2>
          <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
            {authority?.isCurrentDevice
              ? "This device can perform run actions. Other signed-in devices remain view-only."
              : heldElsewhere
                ? "Another device currently controls run actions."
                : "Choose this device before performing run actions."}
          </p>
        </div>
      </div>
      {error && <p role="alert" className="mt-4 rounded-2xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-200">{error}</p>}
      {!authority?.isCurrentDevice && !heldElsewhere && (
        <button type="button" disabled={Boolean(busy)} onClick={() => mutate("claim")} className="mt-4 min-h-11 rounded-xl bg-emerald-700 px-4 font-semibold text-white disabled:opacity-50">
          {busy === "claim" ? "Connecting…" : "Use this device"}
        </button>
      )}
      {heldElsewhere && !confirmTakeover && (
        <button type="button" disabled={Boolean(busy)} onClick={() => setConfirmTakeover(true)} className="mt-4 min-h-11 rounded-xl border border-amber-300 px-4 font-semibold text-amber-800 dark:border-amber-500/40 dark:text-amber-200">
          Move control here
        </button>
      )}
      {heldElsewhere && confirmTakeover && (
        <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-500/30 dark:bg-amber-500/10">
          <div className="flex gap-3"><AlertTriangle className="h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden="true" /><p className="text-sm text-amber-900 dark:text-amber-100">Moving control here immediately blocks run actions from the other device. Server-confirmed work remains saved.</p></div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" disabled={Boolean(busy)} onClick={() => mutate("takeover")} className="min-h-11 rounded-xl bg-amber-700 px-4 font-semibold text-white disabled:opacity-50">{busy === "takeover" ? "Moving…" : "Confirm move"}</button>
            <button type="button" disabled={Boolean(busy)} onClick={() => setConfirmTakeover(false)} className="min-h-11 rounded-xl px-4 font-semibold text-stone-700 dark:text-stone-200">Keep other device</button>
          </div>
        </div>
      )}
    </section>
  );
}
